// ==============================================
// Web Sign-In Link Page (passwordless MyPolaris sign-in)
// ==============================================
//
// Landing page for a MyPolaris sign-in link (?token=<magic-link-token>). The
// login page no longer asks for one: it sends a login code instead
// (services/api.ts sendLoginCode). The API still emails a link here when
// POST /api/auth/sign-in/magic-link is called without a PassageWiki callback or
// a Scout pairing's `das` (vortex-api lib/magicLinkDestination.ts), so the
// page stays.
//
// NOTHING happens on page load. Mail scanners prefetch emailed URLs, so the
// token is only consumed when the person presses the button — the same rule the
// Scout pairing page follows, and the reason both pages exist at all instead of
// linking straight to Better Auth's verify URL.
//
// Unlike the Scout page there is no device to claim: verifying the token
// establishes the cookie session and that is the whole job.

import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { verifyMagicLink } from '../../lib/auth-client';

type Phase = 'ready' | 'working' | 'error';

export function WebSignInLinkPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [phase, setPhase] = useState<Phase>('ready');
  const [error, setError] = useState<string | null>(null);

  const hasToken = useMemo(() => Boolean(token), [token]);

  const handleSignIn = async () => {
    if (!token) return;
    setPhase('working');
    setError(null);
    try {
      const verify = await verifyMagicLink(token);
      if (verify && typeof verify === 'object' && 'error' in verify && (verify as { error?: unknown }).error) {
        setPhase('error');
        setError('This link has expired or has already been used — they only last 15 minutes. Ask for a new one from the sign-in page.');
        return;
      }
      // Full reload rather than a client-side navigate: the auth provider reads
      // the session on boot, and this is the moment it changes.
      window.location.assign('/dashboard');
    } catch {
      setPhase('error');
      setError('This link has expired or has already been used — they only last 15 minutes. Ask for a new one from the sign-in page.');
    }
  };

  return (
    // Same viewport treatment as the other auth pages: bounded to the usable
    // height so the Scout's keyboard cannot push content off-screen.
    <div
      className="flex overflow-y-auto bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4"
      style={{ height: 'var(--vortex-usable-height, 100vh)' }}
    >
      <div className="w-full max-w-md m-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <img src="/vortex-logo-white.svg" alt="Vortex Marine" className="w-10 h-10" />
            <span className="text-2xl font-bold text-white">Vortex Marine</span>
          </div>
          <p className="text-slate-400">MyPolaris sign-in</p>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-8 shadow-xl">
          {!hasToken && (
            <>
              <h1 className="text-xl font-bold text-white text-center mb-3">This link is incomplete</h1>
              <p className="text-sm text-slate-400 text-center">
                Open the link straight from the email without editing it, or ask for a new one from
                the sign-in page.
              </p>
            </>
          )}

          {hasToken && phase !== 'error' && (
            <>
              <h1 className="text-xl font-bold text-white text-center mb-3">Sign in to MyPolaris</h1>
              <p className="text-sm text-slate-400 text-center mb-6">
                Press the button to finish signing in on this device.
              </p>
              <button
                type="button"
                onClick={() => void handleSignIn()}
                disabled={phase === 'working'}
                className="w-full px-4 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold hover:from-cyan-400 hover:to-blue-500 transition-all shadow-lg shadow-cyan-500/25 disabled:opacity-50"
              >
                {phase === 'working' ? 'Signing you in…' : 'Sign in'}
              </button>
            </>
          )}

          {phase === 'error' && (
            <>
              <h1 className="text-xl font-bold text-white text-center mb-3">That link no longer works</h1>
              <p className="text-sm text-slate-400 text-center mb-6">{error}</p>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="w-full px-4 py-3 bg-transparent border border-slate-600 text-slate-300 rounded-xl font-medium hover:border-slate-500 transition-all"
              >
                Back to sign in
              </button>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-slate-400">
          <Link to="/login" className="text-cyan-400 hover:underline">Sign in another way</Link>
        </p>
      </div>
    </div>
  );
}

export default WebSignInLinkPage;
